import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { X } from "lucide-react";
import Breadcrumb from "../components/Breadcrumb";
import Reveal from "../components/Reveal";
import LotCard from "../components/LotCard";
import MarbleImage from "../components/MarbleImage";
import FinalCTA from "../components/sections/FinalCTA";
import { fetchMarbleTypeBySlug, fetchLotsByMarbleSno } from "../lib/api";
import { useSampleRequest } from "../context/SampleRequestContext";

const SkeletonLotCard = () => (
  <div data-testid="type-lot-skeleton" className="bg-brand-surface ring-1 ring-white/5 overflow-hidden">
    <div className="aspect-[16/9] bg-brand-surface animate-pulse" />
    <div className="p-5 md:p-6 space-y-3">
      <div className="h-5 w-24 bg-white/10 animate-pulse rounded" />
      <div className="h-3 w-full bg-white/5 animate-pulse rounded" />
    </div>
  </div>
);

/**
 * Type Page (Phase B - live database data)
 * Shows detailed information about a specific marble type
 * and all available lots of that type.
 */
const TypePage = () => {
  const { slug } = useParams();
  const { openModal } = useSampleRequest();

  const [marbleType, setMarbleType] = useState(null);
  const [lots, setLots] = useState([]);
  const [typeLoading, setTypeLoading] = useState(true);
  const [lotsLoading, setLotsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [heroLightboxOpen, setHeroLightboxOpen] = useState(false);

  const closeHeroLightbox = useCallback(() => setHeroLightboxOpen(false), []);

  // Keyboard handling for the hero lightbox
  useEffect(() => {
    if (!heroLightboxOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") closeHeroLightbox();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [heroLightboxOpen, closeHeroLightbox]);

  // Lock body scroll when the hero lightbox is open
  useEffect(() => {
    if (heroLightboxOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [heroLightboxOpen]);

  useEffect(() => {
    let mounted = true;
    setTypeLoading(true);
    setLotsLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const typeData = await fetchMarbleTypeBySlug(slug);
        if (!mounted) return;
        setMarbleType(typeData);
        setTypeLoading(false);
        try {
          const lotsData = await fetchLotsByMarbleSno(typeData.marble_sno);
          if (mounted) setLots(Array.isArray(lotsData) ? lotsData : []);
        } catch (err) {
          console.error("Failed to load lots:", err);
          if (mounted) setLots([]);
        } finally {
          if (mounted) setLotsLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setNotFound(true);
          setTypeLoading(false);
          setLotsLoading(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (notFound) {
    return (
      <main data-testid="type-not-found" className="min-h-[60vh] flex flex-col items-center justify-center text-center bg-brand-charcoal px-6">
        <p className="font-serif italic text-2xl md:text-3xl text-brand-cream/70 mb-6">
          This marble type could not be found.
        </p>
        <Link
          to="/collection"
          className="font-sans text-xs uppercase tracking-[0.28em] text-brand-gold hover:text-brand-gold/80 transition-colors"
        >
          ← Back to Collection
        </Link>
      </main>
    );
  }

  if (typeLoading || !marbleType) {
    return (
      <main data-testid="type-page-loading" className="bg-brand-charcoal min-h-[60vh]">
        <div className="h-[50vh] md:h-[60vh] bg-brand-surface animate-pulse" />
      </main>
    );
  }

  const breadcrumbItems = [
    { label: "Collection", href: "/collection" },
    { label: marbleType.type_name, href: `/collection/${slug}` }
  ];

  // Left column heading (under "About This Marble" eyebrow) -> column G (About the Marble)
  const characterHeading =
    marbleType.about_the_marble || `${marbleType.type_name} — About This Stone`;

  return (
    <main data-testid="type-page" className="bg-brand-charcoal">
      {/* Breadcrumb */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Hero Section */}
      <section
        data-testid="type-hero"
        role="button"
        tabIndex={0}
        onClick={() => setHeroLightboxOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setHeroLightboxOpen(true);
          }
        }}
        aria-label={`View ${marbleType.type_name} image in full size`}
        className="relative h-[50vh] md:h-[60vh] overflow-hidden cursor-pointer"
      >
        <MarbleImage
          src={marbleType.image_url}
          alt={`${marbleType.type_name} marble`}
          size="hero"
          wrapperClassName="absolute inset-0"
          eager
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal via-brand-charcoal/40 to-transparent" />

        {/* Hero Text */}
        <div className="absolute inset-x-0 bottom-0 editorial-container pb-10 md:pb-14">
          <Reveal>
            {marbleType.short_description && (
              <p
                data-testid="type-hero-origin"
                className="font-sans text-[11px] uppercase tracking-[0.32em] text-brand-gold mb-3"
              >
                {marbleType.short_description}
              </p>
            )}
            <h1
              data-testid="type-hero-name"
              className="font-serif font-light text-brand-cream leading-none tracking-tight"
              style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}
            >
              {marbleType.type_name}
            </h1>
          </Reveal>
        </div>
      </section>

      {/* Hero Lightbox - single image, no arrow navigation */}
      {heroLightboxOpen && (
        <div
          data-testid="hero-lightbox"
          className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex items-center justify-center"
          onClick={closeHeroLightbox}
        >
          <button
            onClick={closeHeroLightbox}
            data-testid="hero-lightbox-close"
            aria-label="Close lightbox"
            className="absolute top-4 right-4 z-10 p-2 text-brand-cream/75 hover:text-brand-cream transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded"
          >
            <X className="w-8 h-8" />
          </button>
          <div
            className="relative max-w-6xl max-h-[85vh] mx-auto px-6 md:px-16"
            onClick={(e) => e.stopPropagation()}
          >
            <MarbleImage
              src={marbleType.image_url}
              alt={`${marbleType.type_name} marble`}
              fill={false}
              eager
              size="hero"
              wrapperClassName="relative w-full h-full"
              className="w-full h-full object-contain"
              data-testid="hero-lightbox-image"
            />
          </div>
        </div>
      )}

      {/* Character Notes Section */}
      <section
        data-testid="type-character-section"
        className="relative border-t border-white/5 py-16 md:py-24"
      >
        <div className="editorial-container">
          <div className="grid md:grid-cols-[40%_60%] gap-8 md:gap-16">
            {/* Left Column: Eyebrow + Heading */}
            <Reveal>
              <div>
                <p className="font-sans text-[11px] uppercase tracking-[0.32em] text-brand-gold mb-5">
                  About This Marble
                </p>
                <h2
                  data-testid="type-character-heading"
                  className="font-serif font-light text-2xl md:text-3xl lg:text-4xl text-brand-cream leading-tight"
                >
                  {characterHeading}
                </h2>
              </div>
            </Reveal>

            {/* Right Column: Body Text + Details Grid */}
            <Reveal delay={0.1}>
              <div>
                {/* Body Text - right column, above the details grid -> column F (Long Description) */}
                {marbleType.long_description && (
                  <div className="space-y-4 mb-10">
                    <p className="font-sans text-base md:text-lg leading-relaxed text-brand-cream/65">
                      {marbleType.long_description}
                    </p>
                  </div>
                )}

                {/* Details Grid */}
                <div
                  data-testid="type-details-grid"
                  className="space-y-3 border-t border-white/5 pt-6"
                >
                  <div className="flex justify-between items-baseline pb-3 border-b border-white/10">
                    <span className="font-sans text-xs uppercase tracking-[0.24em] text-brand-cream/50">
                      Origin
                    </span>
                    <span className="font-sans text-sm md:text-base text-brand-cream/85 text-right max-w-xs">
                      {marbleType.country_of_origin || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline pb-3 border-b border-white/10">
                    <span className="font-sans text-xs uppercase tracking-[0.24em] text-brand-cream/50">
                      Character
                    </span>
                    <span className="font-sans text-sm md:text-base text-brand-cream/85 text-right max-w-xs">
                      {marbleType.character || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline pb-3">
                    <span className="font-sans text-xs uppercase tracking-[0.24em] text-brand-cream/50">
                      Best For
                    </span>
                    <span className="font-sans text-sm md:text-base text-brand-cream/85 text-right max-w-xs">
                      {marbleType.best_for || "-"}
                    </span>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Available Lots Section */}
      <section
        data-testid="type-lots-section"
        className="relative border-t border-white/5 py-16 md:py-24"
      >
        <div className="editorial-container">
          {/* Section Header */}
          <Reveal>
            <div className="mb-12 md:mb-16">
              <p className="font-sans text-[11px] uppercase tracking-[0.32em] text-brand-gold mb-5">
                Available Lots
              </p>
              <h2 className="font-serif font-light text-3xl md:text-4xl lg:text-5xl text-brand-cream leading-tight mb-4">
                Hand-Picked Lots, Ready for Your Project.
              </h2>
              <p className="font-sans text-base md:text-lg text-brand-cream/65 max-w-2xl">
                Every lot photographed in full. Click any lot to see all slabs.
              </p>
            </div>
          </Reveal>

          {/* Lots Grid */}
          {lotsLoading ? (
            <div
              data-testid="type-lots-skeleton-grid"
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            >
              {Array.from({ length: 3 }).map((_, i) => (
                <SkeletonLotCard key={i} />
              ))}
            </div>
          ) : lots.length > 0 ? (
            <div
              data-testid="type-lots-grid"
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            >
              {lots.map((lot, index) => (
                <LotCard
                  key={lot.lot_sno}
                  lot={lot}
                  typeSlug={slug}
                  revealDelay={Math.min(index * 0.08, 0.32)}
                />
              ))}
            </div>
          ) : (
            <div data-testid="type-lots-empty" className="text-center py-20">
              <p className="font-serif italic text-2xl text-brand-cream/50">
                No lots are currently available for this marble.
              </p>
              <p className="mt-3 font-sans text-sm text-brand-cream/35 mb-8">
                WhatsApp us to check live availability.
              </p>
              <a
                href="https://wa.me/918796564619"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="type-lots-empty-whatsapp"
                className="inline-flex items-center gap-2 px-6 py-3 bg-brand-gold text-brand-charcoal font-sans text-xs uppercase tracking-[0.2em] font-medium transition-all hover:bg-brand-gold/90"
                style={{ borderRadius: "5px" }}
              >
                WhatsApp Us
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <FinalCTA
        eyebrow="· Personal Guidance ·"
        heading={
          <>
            Not Quite What You&apos;re Looking For?
          </>
        }
        subtext="Tell us about your project. Our team will curate a selection - and bring samples to your door."
        primary={{ label: "Request Free Samples", onClick: () => openModal([], "type_final_cta") }}
        secondary={{
          label: "WhatsApp Us",
          href: "https://wa.me/918796564619",
          external: true,
        }}
        testIdPrimary="type-cta-request-samples"
        testIdSecondary="type-cta-whatsapp"
      />
    </main>
  );
};

export default TypePage;
